import React, {ReactNode, useState} from 'react';
import Badge from './Badge';

export interface TabItem {
  key: string;
  label: string;
  content: ReactNode;
  icon?: ReactNode;
  description?: string;
  badge?: number | string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
}

interface TabsProps {
  items: TabItem[];
  defaultActiveKey?: string;
  className?: string;
  onChange?: (key: string) => void;
  variant?: 'default' | 'modern';
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  defaultActiveKey,
  className = '',
  onChange,
  variant = 'modern'
}) => {
  const [activeKey, setActiveKey] = useState(defaultActiveKey || items[0]?.key);

  const handleTabChange = (key: string) => {
    setActiveKey(key);
    onChange?.(key);
  };

  const activeTab = items.find(item => item.key === activeKey);
  const isModern = variant === 'modern' || items.some(item => item.icon || item.description || item.badge !== undefined);

  return (
    <div className={`w-full h-full flex flex-col ${className}`}>
      {/* Tab Headers */}
      <div className={`flex ${isModern ? 'border-b border-gray-200 bg-gray-50' : 'border-b border-gray-200'} mb-4 flex-shrink-0`}>
        {items.map((tab) => {
          const isActive = activeKey === tab.key;
          
          if (isModern) {
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-3 px-6 py-4 font-zain font-medium transition-all duration-200 relative ${
                  isActive
                    ? 'bg-white text-primary border-b-2 border-primary shadow-sm'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                {tab.icon && (
                  <span className={`flex-shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-gray-500'}`}>
                    {tab.icon}
                  </span>
                )}
                <div className="flex flex-col items-start space-y-0.5">
                  <span className={`text-base font-semibold font-zain transition-colors ${isActive ? 'text-primary' : 'text-gray-700'}`}>
                    {tab.label}
                  </span>
                  {tab.description && (
                    <span className={`text-xs font-normal font-zain leading-tight transition-colors ${
                      isActive ? 'text-gray-600' : 'text-gray-500'
                    }`}>
                      {tab.description}
                    </span>
                  )}
                </div>
                {tab.badge !== undefined && (
                  <Badge
                    variant={isActive ? (tab.badgeVariant || 'default') : 'secondary'}
                    size="sm"
                    className="flex-shrink-0"
                  >
                    {tab.badge}
                  </Badge>
                )}
              </button>
            );
          }

          // Default variant (backward compatible)
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 font-zain font-medium text-sm transition-colors border-b-2 ${
                isActive
                  ? 'text-primary border-primary'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="w-full flex-1 overflow-hidden">
        {activeTab?.content}
      </div>
    </div>
  );
};

export default Tabs;
